import { useIntl } from 'react-intl'
import PriceRowPointsSpend from '../PriceRowPointsSpend'
import Group from 'components/utils/Group'
import PriceRowPrice from '../PriceRowPrice'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import styled from 'styled-components'
import PriceRowSale from '../PriceRowSale'
import PriceRowPointsSale from '../PriceRowPointsSale'
import Divider from 'LuxKit/Divider'
import PriceRowLoyaltyPoints from '../Loyalty/PriceRowLoyaltyPoints'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import PriceRowValueDiscount from '../Value/PriceRowValueDiscount'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'

const OrText = styled(BodyText)`
  line-height: ${rem(20)};
`

interface Props {
  variant: App.MerchandiseOfferVariant
  quantity: number
  direction: 'horizontal' | 'stack'
  size: 'S' | 'L'
  displayEarnPoints?: boolean
  /**
   * Controls where the discount percentage badge is rendered.
   * - 'inline': next to the strikethrough price (default)
   * - 'above': as a standalone element above the pricing section
   * - 'disabled': not shown at all
   * @default 'inline'
   */
  discountBadgePosition?: 'inline' | 'above' | 'disabled'
}

function PriceRowMerchandisePriceStack(props: Props) {
  const {
    variant,
    quantity,
    size,
    direction,
    displayEarnPoints,
    discountBadgePosition = 'inline',
  } = props

  const [tier] = useCurrentLoyaltyTier()
  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const intl = useIntl()

  const isOnSale = variant.price !== variant.regularPrice
  const isOnPointsSale = variant.pointsPrice !== variant.pointsRegularPrice

  const price = variant.price * quantity
  const points = variant.pointsPrice * quantity
  const regularPrice = variant.regularPrice * quantity
  const pointsRegularPrice = variant.pointsRegularPrice * quantity

  const discountPercent = getValueOffPercent(variant.regularPrice, variant.price)
  const shouldShowDiscountPercent = isOnSale && discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const showInlineDiscount = discountBadgePosition === 'inline'
  const showAboveDiscount = discountBadgePosition === 'above'

  const showEarnPoints = displayEarnPoints && isLoyaltyEnabled && !!variant.pointsEarnRatio
  const orLabel = intl.formatMessage({
    id: '3HTYGb6n5VJ6I02PLjXj',
    defaultMessage: 'or',
    description: 'Text separator between points price and cash price',
  })

  const innerContent = <Group
    direction={direction === 'horizontal' ? 'horizontal' : 'vertical'}
    gap={direction === 'horizontal' ? 8 : (size === 'S' ? 4 : 8)}
    tabletGap={24}
    verticalAlign={direction === 'horizontal' ? 'start' : undefined}
  >
    <Group direction="horizontal" gap={8} verticalAlign="start">
      <div>
        {!!points && <PriceRowPointsSpend points={points} size={size} />}
        {isOnPointsSale && <Group direction="horizontal" gap={8} verticalAlign="center">
          <PriceRowPointsSale value={pointsRegularPrice} colour="neutral-three" />
          {showInlineDiscount && shouldShowDiscountPercent && <PriceRowValueDiscount discountPercentage={discountPercent} type="exact" />}
        </Group>}
      </div>
    </Group>
    <Divider kind="primary" direction="vertical" />
    <Group direction="horizontal" gap={4}>
      {!!price && !!points && <OrText variant="small" colour="neutral-three">{orLabel}</OrText>}
      <div>
        {!!price && <PriceRowPrice data-testid="merch-pdp-price-amount" price={price} size={size} saleUnit="" priceFormat="dynamicDollar" />}
        {isOnSale && <Group direction="horizontal" gap={8} verticalAlign="center">
          <PriceRowSale value={regularPrice} colour="neutral-three" priceFormat="dynamicDollar" />
          {showInlineDiscount && shouldShowDiscountPercent && <PriceRowValueDiscount discountPercentage={discountPercent} type="exact" />}
        </Group>}
        {showEarnPoints && (
          <PriceRowLoyaltyPoints
            tier={tier}
            size="S"
            earnablePoints={0}
            pointsEarnRatio={variant.pointsEarnRatio}
            calculationType="ratio"
          />
        )}
      </div>
    </Group>
  </Group>

  if (showAboveDiscount && shouldShowDiscountPercent) {
    return <Group direction="vertical" gap={16}>
      <PriceRowValueDiscount discountPercentage={discountPercent} type="exact" />
      {innerContent}
    </Group>
  }

  return innerContent
}

export default PriceRowMerchandisePriceStack
